import { Link } from "react-router-dom";
import { HeartHandshake, Users, ArrowRight, ShieldCheck, FileText, MapPin, Phone, BadgeCheck } from "lucide-react";
import { useLang } from "../../i18n";
import { useContent } from "../../lib/useContent";
import DonateButton from "../DonateButton";
import { Reveal, SectionHeading } from "../Reveal";
import DonateCard from "../DonateCard";

export function VolunteerCta() {
  const { pick } = useLang();
  return (
    <section data-testid="volunteer-cta-section" className="py-24 bg-ngo-cream relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="relative bg-ngo-mint border border-ngo-deep/10 rounded-[2.5rem] px-8 py-14 sm:px-14 overflow-hidden grain">
          <span className="absolute -bottom-10 -right-6 font-heading font-black text-[10rem] leading-none text-ngo-deep/5 select-none" aria-hidden="true">जुड़ें</span>
          <div className="max-w-2xl relative">
            <SectionHeading
              overline={pick({ hi: "स्वयंसेवा", en: "Volunteer" })}
              title={pick({ hi: "हमसे जुड़ें – बदलाव का हिस्सा बनें", en: "Join Us – Be Part of the Change" })}
              description={pick({
                hi: "समाज में बदलाव केवल एक संस्था से नहीं, बल्कि लोगों की भागीदारी से संभव है। आप स्वयंसेवक के रूप में हमारे साथ जुड़कर शिक्षा, स्वास्थ्य, पर्यावरण, महिला सशक्तिकरण और सामाजिक जागरूकता गतिविधियों में योगदान दे सकते हैं।",
                en: "Change in society is possible not just through one organization, but through people's participation. Join us as a volunteer and contribute to education, health, environment, women empowerment and social awareness activities.",
              })}
              testid="volunteer-cta-heading"
            />
            <Reveal i={3} className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/volunteer"
                data-testid="volunteer-cta-btn"
                className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-ngo-deep text-white font-semibold hover:bg-ngo-forest hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
              >
                <Users size={17} />
                {pick({ hi: "स्वयंसेवक बनें", en: "Become a Volunteer" })}
              </Link>
              <Link
                to="/contact"
                data-testid="volunteer-contact-btn"
                className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full border-2 border-ngo-deep text-ngo-deep font-semibold hover:bg-ngo-deep hover:text-white transition-colors duration-300"
              >
                {pick({ hi: "संपर्क करें", en: "Contact Us" })}
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

export function DonateCta() {
  const { t } = useLang();
  return (
    <section data-testid="donate-cta-section" className="py-24 bg-ngo-forest relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[30rem] h-[30rem] rounded-full bg-ngo-deep/40 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading
              dark
              overline={t("nav.donate")}
              title={t("donate.title")}
              description={t("donate.sub")}
              testid="donate-cta-heading"
            />
            <Reveal i={3} className="mt-8">
              <p className="text-ngo-mint/75 text-sm leading-relaxed border-l-2 border-ngo-gold pl-4">{t("donate.trust")}</p>
            </Reveal>
          </div>
          <DonateCard compact />
        </div>
      </div>
    </section>
  );
}

export function SpecialCta() {
  const { pick } = useLang();
  return (
    <section data-testid="special-cta-section" className="py-28 bg-ngo-cream relative overflow-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <Reveal>
          <HeartHandshake size={44} className="mx-auto text-ngo-gold" />
        </Reveal>
        <Reveal i={1}>
          <h2 data-testid="special-cta-heading" className="mt-6 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ngo-ink leading-tight">
            {pick({ hi: "बदलाव की शुरुआत हमसे और आपसे होती है।", en: "Change begins with us — and with you." })}
          </h2>
        </Reveal>
        <Reveal i={2}>
          <p className="mt-6 text-base md:text-lg text-ngo-ink/70 leading-relaxed max-w-2xl mx-auto">
            {pick({
              hi: "शिक्षा, स्वास्थ्य, पर्यावरण और समाज सेवा की इस पहल में आपका सहयोग हमारे लिए महत्वपूर्ण है। आइए, मिलकर एक बेहतर और आत्मनिर्भर समाज के निर्माण की दिशा में कदम बढ़ाएं।",
              en: "Your support matters in this initiative of education, health, environment and social service. Let us together take steps towards building a better and self-reliant society.",
            })}
          </p>
        </Reveal>
        <Reveal i={3} className="mt-9 flex flex-wrap justify-center gap-4">
          <DonateButton label={pick({ hi: "दान करें", en: "Donate" })} variant="gold" size="lg" testid="special-donate-btn" className="px-8 py-4 font-bold" />
          <Link
            to="/volunteer"
            data-testid="special-volunteer-btn"
            className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
          >
            {pick({ hi: "स्वयंसेवक बनें", en: "Become a Volunteer" })}
            <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export function Transparency() {
  const { pick, lang } = useLang();
  const c = useContent();
  const rows = [
    { icon: BadgeCheck, label: pick({ hi: "पंजीकरण संख्या", en: "Registration Number" }), value: c.reg_no, testid: "trans-reg" },
    { icon: FileText, label: pick({ hi: "स्थापना वर्ष", en: "Year of Establishment" }), value: c.established, testid: "trans-est" },
    { icon: MapPin, label: pick({ hi: "कार्यालय पता", en: "Office Address" }), value: lang === "hi" ? c.address_hi : c.address_en, testid: "trans-address" },
    { icon: Phone, label: pick({ hi: "संपर्क", en: "Contact" }), value: `${c.phone} • ${c.email}`, testid: "trans-contact" },
  ];
  return (
    <section data-testid="transparency-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <SectionHeading
              overline={pick({ hi: "विश्वास", en: "Trust" })}
              title={pick({ hi: "पारदर्शिता और विश्वास", en: "Transparency & Trust" })}
              description={pick({
                hi: "पंजीकृत संस्था • स्थापना 2025 • वास्तविक जमीनी गतिविधियां • पारदर्शी संपर्क जानकारी। हम अपनी सभी जानकारी खुलकर साझा करते हैं।",
                en: "Registered organization • Established 2025 • Real grassroots activities • Transparent contact information. We openly share all our details.",
              })}
              testid="transparency-heading"
            />
            <div className="mt-8 space-y-4">
              {rows.map((r) => (
                <Reveal key={r.testid}>
                  <div className="flex items-start gap-4 bg-ngo-cream border border-ngo-deep/10 rounded-2xl p-5">
                    <div className="w-10 h-10 rounded-xl bg-ngo-mint flex items-center justify-center text-ngo-deep shrink-0">
                      <r.icon size={19} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold tracking-wide uppercase text-ngo-leaf">{r.label}</p>
                      <p data-testid={r.testid} className="mt-1 text-sm font-semibold text-ngo-ink break-all">{r.value}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="space-y-6">
            <Reveal>
              <div data-testid="testimonial-placeholder" className="bg-ngo-mint border border-dashed border-ngo-leaf/40 rounded-3xl p-10 text-center">
                <ShieldCheck size={36} className="mx-auto text-ngo-deep/50" />
                <h3 className="mt-4 font-heading font-bold text-xl text-ngo-ink">{pick({ hi: "प्रतिक्रियाएं", en: "Testimonials" })}</h3>
                <p className="mt-3 text-ngo-ink/60 text-sm leading-relaxed">{pick({ hi: "आपकी प्रतिक्रिया जल्द यहां प्रदर्शित की जाएगी।", en: "Genuine testimonials will be displayed here soon." })}</p>
              </div>
            </Reveal>
            <Reveal i={1}>
              <div data-testid="video-placeholder" className="bg-ngo-forest rounded-3xl p-10 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-ngo-gold/20 flex items-center justify-center">
                  <div className="w-0 h-0 border-t-8 border-b-8 border-l-12 border-l-[14px] border-transparent border-l-ngo-gold ml-1" />
                </div>
                <h3 className="mt-4 font-heading font-bold text-xl text-ngo-cream">{pick({ hi: "हमारे कार्यों की झलक", en: "Glimpses of Our Work" })}</h3>
                <p className="mt-3 text-ngo-mint/60 text-sm">{pick({ hi: "वीडियो जल्द उपलब्ध होगा", en: "Videos coming soon" })}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
